import { RailRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRow";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  useAutomationObjectActionDeps,
  useAutomationObjectActionHandlers,
  useObjectActions,
  type ObjectKind,
} from "@/object-actions";
import { describeCadence } from "@/panes/automation/cadence";
import { useOpenAutomation } from "@/panes/automation/useOpenAutomation";
import { cn } from "@/utils";
import { type FlowSchedule } from "@cohesive/api-client";
import { Zap } from "lucide-react";

const AUTOMATION_KIND: ObjectKind = { type: "automation" };

/** Skills' enabled-dot register: green = active, dormant grey = paused (never red). */
function AutomationStateDot({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "ml-1 size-1.5 shrink-0 rounded-full",
        active ? "bg-lc-active" : "bg-n-40",
      )}
      aria-hidden
    />
  );
}

export function AutomationsRailRow({
  schedule,
  active,
}: {
  schedule: FlowSchedule;
  active?: boolean;
}) {
  const openAutomation = useOpenAutomation();
  const menu = useRailRowContextMenu();

  const cadence = describeCadence(schedule);
  const isActive = cadence.state === "active";

  const deps = useAutomationObjectActionDeps(schedule, {
    isPaneActive: !!active,
  });
  const handlers = useAutomationObjectActionHandlers(deps);
  const { actions } = useObjectActions({
    kind: AUTOMATION_KIND,
    surface: "left-rail",
    enabled: menu.open,
    state: { isAutomationActive: isActive },
    handlers,
  });

  return (
    <div
      className="group/row relative"
      onContextMenu={menu.onContextMenu}
      data-rail-automation-row
    >
      <RailRow
        icon={Zap}
        label={schedule.name}
        active={active}
        className="select-none pr-8"
        trailing={<AutomationStateDot active={isActive} />}
        // A paused automation keeps showing its retained cadence so resume
        // stays legible; a never-scheduled one shows the dot alone.
        meta={cadence.cadence ?? undefined}
        onClick={() => openAutomation(schedule.id)}
      />

      {/* Registry-driven (surface `left-rail`). The menu stays non-modal so
          the delete AlertDialog can stack without Radix freezing <body>. */}
      <ObjectActionMenu
        actions={actions}
        kind={AUTOMATION_KIND}
        name={schedule.name}
        menuController={menu}
        label={schedule.name}
      />
    </div>
  );
}
